import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ConfigProvider, Flex, theme } from "antd";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useTranslate,
} from "@refinedev/core";
import { useIsMutating } from "@tanstack/react-query";
import {
  Card,
  CTAButton,
  ExperienceCanvas,
  libertifyThemeV2,
  PublicationStatusBar,
} from "@libertify/ui";
import { QuotaUpgradeModal } from "components/QuotaUpgradeModal";
import type { AddOnType } from "pages/media/types";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import { usePublishUnpublish } from "pages/media/projects/hooks/usePublishUnpublish";
import { useRegenerateOrApprove } from "pages/media/projects/hooks/useRegenerateOrApprove";
import { useOutOfDateDismissal } from "pages/media/projects/hooks/useOutOfDateDismissal";
import { useWsSubscription } from "hooks/useWsSubscription";
import type { WSEvent } from "store/websocketStore";
import { useRegenerateOutOfDateItems } from "../hooks/useRegenerateOutOfDateItems";
import {
  ADDON_CATALOG,
  CHAT_ADDON_ID,
  CONFIG_ITEM,
  EDIT_SETTINGS_ITEMS,
  UI_ONLY_ADDON_IDS,
  type AddonId,
} from "pages/create-experience/data/addons";
import {
  canShowAddon,
  readConfiguredAddOns,
  type ProjectAddOnsResponse,
} from "pages/create-experience/data/addonApi";
import { AddonRail } from "pages/create-experience/components/AddonRail";
import { VideoEditCard } from "pages/create-experience/components/config-cards/VideoEditCard";
import { DocumentsEditCard } from "pages/create-experience/components/config-cards/DocumentsEditCard";
import { ChatbotEditCard } from "pages/create-experience/components/config-cards/ChatbotEditCard";
import { PodcastEditCard } from "pages/create-experience/components/config-cards/PodcastEditCard";
import { KeyPointsEditCard } from "pages/create-experience/components/config-cards/key-points/KeyPointsEditCard";
import { GeneralSettingsEditCard } from "pages/create-experience/components/config-cards/GeneralSettingsEditCard";
import { QuickRecallEditCard } from "pages/create-experience/components/config-cards/quick-recall/QuickRecallEditCard";
import { QuizEditCard } from "pages/create-experience/components/config-cards/quiz/QuizEditCard";
import { StandoutsEditCard } from "pages/create-experience/components/config-cards/standouts/StandoutsEditCard";
import type { ConfigItemId } from "pages/create-experience/components/config-cards/registry";
import { readEditNavTab } from "../editNavigation";
import {
  Shell,
  Title,
  Body,
  RightCol,
  CardTitle,
  CardPlaceholder,
  EmptyBody,
} from "pages/create-experience/components/CustomizePanel.styles";

const UI_ONLY_IDS: ReadonlySet<string> = new Set(UI_ONLY_ADDON_IDS);

/** Add-on ids ARE the backend `AddOnType` values, minus the UI-only prototypes
 *  (which have no backend add-on behind them). */
const toAddOnTypes = (ids: AddonId[]): AddOnType[] =>
  ids.filter((id): id is Extract<AddonId, AddOnType> => !UI_ONLY_IDS.has(id));

const labelKeyFor = (id: ConfigItemId): string => {
  const settings = EDIT_SETTINGS_ITEMS.find((s) => s.id === id);
  if (settings) return settings.labelKey;
  return ADDON_CATALOG.find((a) => a.id === id)?.labelKey ?? "";
};

/**
 * Creator-mode edit page for an existing experience.
 *
 * The rail lists the experience's add-ons and lets each be shown or hidden
 * (rather than removed, which is the create flow's action). Both showing/hiding
 * and reordering PATCH `add-ons/enabled` with the full list: that endpoint
 * replaces the set rather than taking a per-add-on flag, and the list is
 * ordered, so its sequence carries the arrangement.
 *
 * The create flow's config cards are not reused here directly: they read the
 * PlaybookDraft context, which only the create flow provides. Every edit card
 * fetches the existing project and saves each change on its own (see
 * `VideoEditCard`, `DocumentsEditCard`, `GeneralSettingsEditCard`,
 * `ChatbotEditCard`, `PodcastEditCard`, `QuickRecallEditCard` and
 * `KeyPointsEditCard` — the general one shares its field sections with the
 * create-flow card, just not the data source; Quick recall and Key points
 * edit generated CONTENT rather than settings). Every other item still shows
 * a placeholder.
 */
export const ExperienceEditPage = () => (
  // Scopes theme-v2 tokens (so every antd `theme.useToken()` consumer
  // below — PublicationStatusBar, AddonRail, the edit cards, Card, the
  // `$token`-driven styles in `CustomizePanel.styles.ts`, …) and the
  // `theme.useToken()` call inside `ExperienceEditPageContent` all read
  // from this provider, matching `ExperiencePage`'s own wrap, so the Edit
  // page shares chrome with the View page instead of AntD's stock
  // default theme. Split into its own component so `theme.useToken()`
  // can be called from a descendant of this provider rather than from
  // the component that declares it.
  <ConfigProvider theme={libertifyThemeV2}>
    <ExperienceCanvas>
      <ExperienceEditPageContent />
    </ExperienceCanvas>
  </ConfigProvider>
);

const ExperienceEditPageContent = () => {
  const { projectId, instanceId } = useParams<{
    projectId: string;
    instanceId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslate();
  const { token } = theme.useToken();
  const apiUrl = useApiUrl();
  // Mirrors ExperiencePage: the route's :instanceId is optional there, and
  // "default" is the instance it falls back to.
  const effectiveInstanceId = instanceId ?? "default";

  // Both halves of the rail's state come from this one read: which add-ons the
  // project has (settings branches) and which of them viewers see (enabled).
  const { data, refetch } = useCustom<ProjectAddOnsResponse>({
    url: `${apiUrl}/media/projects/${projectId}/add-ons`,
    method: "get",
    queryOptions: { enabled: Boolean(projectId) },
  });
  const visibleAddOns = useMemo<Extract<AddonId, AddOnType>[]>(
    // "General" is the fixed general settings entry, not a rail add-on row.
    () =>
      (data?.data?.enabled?.enabled_add_ons ?? []).filter(
        (type): type is Extract<AddonId, AddOnType> => type !== "General"
      ),
    [data?.data]
  );
  /** Add-ons that exist on the project — shown or hidden, they stay in the rail. */
  const configuredAddOns = useMemo<Extract<AddonId, AddOnType>[]>(
    () => readConfiguredAddOns(data?.data?.settings),
    [data?.data]
  );

  const { mutateAsync } = useCustomMutation();
  const {
    markAsOutOfDate,
    project,
    media,
    isLive,
    isPublicationOutOfDate,
    isMediaOutOfDate,
    isOutOfDateStatusLoading,
    invalidateProject,
  } = useProjectInstance({
    projectId: projectId ?? "",
    instanceId: effectiveInstanceId,
  });

  // ── Header status bar: saving indicator + the one publication action ──
  // Same wiring as ExperiencePage's out-of-date widget, so both surfaces
  // agree on what "Regenerate" and "Update" mean.
  const storyboard = project?.storyboards.find(
    (s) => s.id === media?.storyboard_settings_id
  );
  const { regenerateOrApprove, isProcessing: isRegenerating } =
    useRegenerateOrApprove({
      projectId,
      storyboardSettingsId: media?.storyboard_settings_id,
      storyboardStatus: storyboard?.status,
      mediaId: media?.id ?? undefined,
      mediaStatus: media?.status,
      refetchProjectDetail: () => void invalidateProject(),
    });
  const {
    isAddOnsOutOfDate,
    regenerateOutOfDateItems,
    isRegeneratingAddOns,
  } = useRegenerateOutOfDateItems({
    projectId,
    project,
    isMediaOutOfDate,
    regenerateOrApprove,
    invalidateProject,
  });
  // Refetch the project on any of its WS events (like ExperiencePage), so an
  // add-on going OutOfDate after a settings save shows up without a reload.
  const projectSubscriptionConfig = useMemo(
    () => (projectId ? { type: "project" as const, id: projectId } : []),
    [projectId]
  );
  const handleProjectEvent = useCallback(
    (event: WSEvent) => {
      if (event.project_id !== projectId) return;
      invalidateProject().catch((error) => {
        console.error(
          "[ExperienceEditPage] error refetching project after WS event",
          error
        );
      });
    },
    [projectId, invalidateProject]
  );
  useWsSubscription(projectSubscriptionConfig, handleProjectEvent);
  const {
    republish,
    isMutatingStatus: isPublishing,
    showQuotaModal,
    dismissQuotaModal,
  } = usePublishUnpublish(projectId ?? "", effectiveInstanceId);
  // Routed through the dismissal bookkeeping so a successful action from here
  // also un-dismisses the experience page's floating widget.
  const { handleRegenerate, handleUpdate } = useOutOfDateDismissal({
    projectId: projectId ?? "",
    onRegenerate: regenerateOutOfDateItems,
    onUpdate: async () => (await republish()).success,
  });
  // Every card saves through React Query — Refine shares the app's client —
  // so the in-flight mutation count is the page-wide "saving" signal. The
  // publish/regenerate mutations are excluded: the bar shows those on its
  // own button.
  const isSaving =
    useIsMutating() > 0 &&
    !isPublishing &&
    !isRegenerating &&
    !isRegeneratingAddOns;
  // The project's storyboard settings id — the same one the video editors are
  // addressed by (see `VideoEditCard`).
  const storyboardSettingsId = project?.storyboards[0]?.id;
  // Add-ons with a request in flight, counted per id so one request finishing
  // doesn't re-enable a row another request still holds. The ref mirrors the
  // state so a second click inside the same render is blocked before React
  // re-renders.
  const [pendingIds, setPendingIds] = useState<AddonId[]>([]);
  const pendingRef = useRef<ReadonlyMap<AddonId, number>>(new Map());
  const updatePending = (id: AddonId, delta: 1 | -1) => {
    const next = new Map(pendingRef.current);
    const count = (next.get(id) ?? 0) + delta;
    if (count > 0) next.set(id, count);
    else next.delete(id);
    pendingRef.current = next;
    setPendingIds([...next.keys()]);
  };
  const markPending = (id: AddonId) => updatePending(id, 1);
  const clearPending = (id: AddonId) => updatePending(id, -1);
  // Generate requests only, so a visibility PATCH on the same row can't block one.
  const generatingRef = useRef(new Set<AddonId>());
  // Deep-linked from the experience page's Edit CTA via router state (see
  // editNavigation.ts) — read once on mount, same as the rest of this
  // component's initial state. A refresh loses it and falls back to General,
  // which is an accepted trade-off for not round-tripping the active card
  // through the URL (see editNavigation.ts).
  const [active, setActive] = useState<ConfigItemId>(
    () => readEditNavTab(location.state) ?? CONFIG_ITEM.General
  );
  /**
   * The enabled list as last sent, held until the refetch catches up. Toggling a
   * second add-on before the first response lands would otherwise rebuild the
   * payload from stale server state and switch the first one back on.
   */
  const sentVisible = useRef<AddOnType[] | null>(null);
  /**
   * Add-ons picked from the modal that the project doesn't carry settings for
   * yet, so the row appears straight away.
   *
   * TODO(MED-6947): session-only — an add-on only truly exists once it has
   * settings, so adding one needs a settings PATCH to create it. Wire that with
   * the rest of the settings persistence.
   */
  const [addedIds, setAddedIds] = useState<AddonId[]>([]);
  /** Arrangement applied immediately on drag, so the row doesn't snap back
   *  while its PATCH is in flight. */
  const [order, setOrder] = useState<AddonId[] | null>(null);

  const addonIds = useMemo<AddonId[]>(() => {
    // Visible ones first, in their stored arrangement, then the rest of the
    // project's add-ons (hidden, so unordered by the API), then session adds.
    const present: AddonId[] = [...visibleAddOns];
    for (const id of [...configuredAddOns, ...addedIds]) {
      if (!present.includes(id)) present.push(id);
    }
    if (!order) return present;
    // Honour the dragged order, dropping ids that no longer exist and appending
    // any that showed up since (e.g. after a refetch).
    const ordered = order.filter((id) => present.includes(id));
    return [...ordered, ...present.filter((id) => !ordered.includes(id))];
  }, [visibleAddOns, configuredAddOns, addedIds, order]);

  // Reads through the in-flight list so back-to-back toggles compose instead of
  // each rebuilding from the server's not-yet-updated answer.
  const currentVisible = () => sentVisible.current ?? visibleAddOns;
  const isVisible = (id: AddonId) => currentVisible().includes(id as AddOnType);

  /**
   * Sends the full enabled list, which carries both the set and its
   * arrangement. Pessimistic for visibility: the eye only flips once the PATCH
   * lands and the read refetches, so a failed request can't leave the icon
   * contradicting the backend. Resolves `false` when the request failed, so the
   * caller can undo whatever it applied optimistically.
   */
  const patchVisibleAddOns = async (
    next: AddonId[],
    pendingRow: AddonId
  ): Promise<boolean> => {
    const payload = toAddOnTypes(next);
    const chatChanged =
      currentVisible().includes(CHAT_ADDON_ID) !==
      payload.includes(CHAT_ADDON_ID);
    sentVisible.current = payload;
    markPending(pendingRow);
    try {
      await mutateAsync({
        url: `${apiUrl}/media/projects/${projectId}/add-ons/enabled`,
        method: "patch",
        values: { enabled_add_ons: payload },
        successNotification: false,
      });
      // A live publication bakes chat in at publish time, so it keeps its chat
      // until regenerated — the user has to be told. No-ops when nothing is
      // live, and notifies on its own.
      if (chatChanged) await markAsOutOfDate().catch(() => undefined);
      await refetch();
      return true;
    } catch {
      // useCustomMutation surfaces the error notification itself.
      return false;
    } finally {
      sentVisible.current = null;
      clearPending(pendingRow);
    }
  };

  /**
   * Starts the video generation for an experience that has never produced one.
   * A project created without Video has no media at all (its entry is written
   * when generation starts), so enabling the add-on would otherwise leave a tab
   * with nothing to show.
   *
   * One call covers the whole chain — storyboard, slides and media — so nothing
   * has to be sequenced behind it. `regenerate_script_specs` is left at its
   * default (false) so already-generated script specs are reused.
   */
  const generateVideoIfMissing = async () => {
    // A media entry means the video exists or is being produced: re-enabling
    // it just reveals the tab, and regenerating would throw away the creator's
    // edits or charge credits twice. Only a failed one is retried.
    if (media?.url || (media && media.status !== "Error")) return;
    if (!storyboardSettingsId || generatingRef.current.has(CONFIG_ITEM.Video)) {
      return;
    }
    generatingRef.current.add(CONFIG_ITEM.Video);
    markPending(CONFIG_ITEM.Video);
    try {
      await mutateAsync({
        url: `${apiUrl}/media/projects/${projectId}/storyboards/${storyboardSettingsId}/regenerate`,
        method: "post",
        values: {},
        successNotification: false,
      });
      await invalidateProject();
    } catch {
      // useCustomMutation surfaces the error notification itself.
    } finally {
      generatingRef.current.delete(CONFIG_ITEM.Video);
      clearPending(CONFIG_ITEM.Video);
    }
  };

  /**
   * Starts a non-Video add-on's generation when it never ran (no state yet) or
   * failed (MED-7185, MED-7292). `add-ons/enabled` never generates on its own,
   * so without this the add-on is shown with no content. Only the one type is
   * sent: an empty `add_on_types` regenerates every enabled add-on.
   */
  const generateAddOnIfMissing = async (id: AddonId, addOnType: AddOnType) => {
    if (!storyboardSettingsId || generatingRef.current.has(id)) return;
    const status = project?.storyboards
      .find((s) => s.id === storyboardSettingsId)
      ?.add_ons?.find((addOn) => addOn.add_on_type === addOnType)?.status;
    // Already generated (or in flight): re-showing it just reveals it again.
    if (status && status !== "Error") return;
    generatingRef.current.add(id);
    markPending(id);
    try {
      await mutateAsync({
        url: `${apiUrl}/media/projects/${projectId}/add-ons/generate`,
        method: "post",
        values: {
          add_on_types: [addOnType],
          storyboard_settings_id: storyboardSettingsId,
        },
        successNotification: false,
      });
      await invalidateProject();
    } catch {
      // useCustomMutation surfaces the error notification itself.
    } finally {
      generatingRef.current.delete(id);
      clearPending(id);
    }
  };

  // Built from the rail's display order (not the API's) so showing or hiding an
  // add-on preserves the arrangement the user is looking at.
  const handleVisibilityChange = (id: AddonId, visible: boolean) => {
    // An add-on whose settings can't generate it isn't shown until they can —
    // its card says what's missing.
    if (visible && !canShowAddon(id, data?.data.settings)) {
      void showIfAllowedAfterRefetch(id);
      return;
    }
    applyVisibility(id, visible);
  };

  // This render's read can predate settings the card just saved, so re-read
  // before refusing.
  const showIfAllowedAfterRefetch = async (id: AddonId) => {
    setActive(id);
    markPending(id);
    const fresh = await refetch().finally(() => clearPending(id));
    if (canShowAddon(id, fresh.data?.data.settings)) applyVisibility(id, true);
  };

  const applyVisibility = (id: AddonId, visible: boolean) => {
    const next = visible
      ? addonIds.filter((current) => current === id || isVisible(current))
      : addonIds.filter((current) => current !== id && isVisible(current));
    // Pin the arrangement, otherwise a hidden add-on drops out of the enabled
    // list and the memo re-appends its row at the bottom of the rail.
    setOrder(addonIds);
    void patchVisibleAddOns(next, id).then((ok) => {
      // Only once the add-on is actually enabled, so a failed save can't leave
      // a generation running for a video the experience doesn't offer.
      if (!ok || !visible) return;
      if (id === CONFIG_ITEM.Video) {
        void generateVideoIfMissing();
        return;
      }
      // UI-only prototypes have no backend add-on to generate.
      const [addOnType] = toAddOnTypes([id]);
      if (addOnType) void generateAddOnIfMissing(id, addOnType);
    });
  };

  const handleReorderAddons = (from: number, to: number) => {
    const next = [...addonIds];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    const previousOrder = order;
    setOrder(next);
    // Moving a hidden row can't change the enabled list — its position isn't
    // representable there — so don't fire a PATCH that would say nothing while
    // showing a spinner on the row.
    if (!isVisible(moved)) return;
    void patchVisibleAddOns(next.filter(isVisible), moved).then((ok) => {
      if (!ok) setOrder(previousOrder);
    });
  };

  const handleAddAddons = (ids: AddonId[]) => {
    setAddedIds((current) => [
      ...current,
      ...ids.filter((id) => !current.includes(id)),
    ]);
    if (ids[0]) setActive(ids[0]);
  };

  const renderActiveCard = () => {
    if (active === CONFIG_ITEM.Video) {
      return (
        <VideoEditCard
          visible={isVisible(CONFIG_ITEM.Video)}
          visiblePending={pendingIds.includes(CONFIG_ITEM.Video)}
          onVisibleChange={(visible) =>
            handleVisibilityChange(CONFIG_ITEM.Video, visible)
          }
        />
      );
    }
    if (active === CONFIG_ITEM.Documents) {
      return <DocumentsEditCard />;
    }
    if (active === CONFIG_ITEM.General) {
      return (
        <GeneralSettingsEditCard
          key={`${projectId}-${effectiveInstanceId}`}
          hasChatAddon={isVisible(CHAT_ADDON_ID)}
        />
      );
    }
    if (active === CHAT_ADDON_ID) {
      return (
        <ChatbotEditCard
          visible={isVisible(CHAT_ADDON_ID)}
          visiblePending={pendingIds.includes(CHAT_ADDON_ID)}
          onVisibleChange={(visible) =>
            handleVisibilityChange(CHAT_ADDON_ID, visible)
          }
        />
      );
    }
    if (active === CONFIG_ITEM.Podcast) {
      return (
        <PodcastEditCard
          visible={isVisible(CONFIG_ITEM.Podcast)}
          visiblePending={pendingIds.includes(CONFIG_ITEM.Podcast)}
          onVisibleChange={(visible) =>
            handleVisibilityChange(CONFIG_ITEM.Podcast, visible)
          }
        />
      );
    }
    if (active === CONFIG_ITEM.Flashcards) {
      return (
        <QuickRecallEditCard
          visible={isVisible(CONFIG_ITEM.Flashcards)}
          visiblePending={pendingIds.includes(CONFIG_ITEM.Flashcards)}
          onVisibleChange={(visible) =>
            handleVisibilityChange(CONFIG_ITEM.Flashcards, visible)
          }
        />
      );
    }
    // `Summary` is the backend add-on type behind the "Key points" label.
    if (active === CONFIG_ITEM.Summary) {
      return (
        <KeyPointsEditCard
          visible={isVisible(CONFIG_ITEM.Summary)}
          visiblePending={pendingIds.includes(CONFIG_ITEM.Summary)}
          onVisibleChange={(visible) =>
            handleVisibilityChange(CONFIG_ITEM.Summary, visible)
          }
        />
      );
    }
    if (active === CONFIG_ITEM.Quiz) {
      return (
        <QuizEditCard
          visible={isVisible(CONFIG_ITEM.Quiz)}
          visiblePending={pendingIds.includes(CONFIG_ITEM.Quiz)}
          onVisibleChange={(visible) =>
            handleVisibilityChange(CONFIG_ITEM.Quiz, visible)
          }
        />
      );
    }
    if (active === CONFIG_ITEM.Standouts) {
      return (
        <StandoutsEditCard
          visible={isVisible(CONFIG_ITEM.Standouts)}
          visiblePending={pendingIds.includes(CONFIG_ITEM.Standouts)}
          onVisibleChange={(visible) =>
            handleVisibilityChange(CONFIG_ITEM.Standouts, visible)
          }
        />
      );
    }
    return (
      <Card>
        <CardTitle $token={token}>{t(labelKeyFor(active))}</CardTitle>
        <CardPlaceholder $token={token}>
          {t("createExperience.customize.placeholder")}
        </CardPlaceholder>
        <EmptyBody />
      </Card>
    );
  };

  return (
    // No "Create now" bar on this page — its only bar, PublicationStatusBar,
    // sits inline in the header — so the shell needs no bottom clearance.
    <Shell $hasCreateBar={false}>
      {/* Creator mode hides the admin sidebar (MED-6950), so this page owns
          its only way out — back to the experience it edits. */}
      <Flex align="center" justify="space-between" gap={16} wrap="wrap">
        <CTAButton
          icon={<ArrowLeftIcon size={14} />}
          onClick={() =>
            navigate(
              `/media/projects/${projectId}/experience-v2/${effectiveInstanceId}`
            )
          }
        >
          {t("buttons.back")}
        </CTAButton>
        <PublicationStatusBar
          isSaving={isSaving}
          isLoading={isOutOfDateStatusLoading}
          isLive={isLive}
          isItemsOutOfDate={isMediaOutOfDate || isAddOnsOutOfDate}
          isPublicationOutOfDate={isPublicationOutOfDate}
          onRegenerate={handleRegenerate}
          onUpdate={handleUpdate}
        />
      </Flex>

      <Title $token={token}>{t("experienceEdit.title")}</Title>

      <Body>
        <AddonRail
          settingsItems={EDIT_SETTINGS_ITEMS}
          addonIds={addonIds}
          active={active}
          onSelect={setActive}
          onAddAddons={handleAddAddons}
          onReorderAddons={handleReorderAddons}
          rowAction={{
            kind: "visibility",
            isVisible,
            pendingIds,
            onVisibilityChange: handleVisibilityChange,
          }}
        />

        <RightCol>{renderActiveCard()}</RightCol>
      </Body>
      {/* Publishing from the status bar can hit the publication quota. */}
      <QuotaUpgradeModal open={showQuotaModal} onClose={dismissQuotaModal} />
    </Shell>
  );
};

export default ExperienceEditPage;
